import type { ObjectMetadata } from '@olytic/foundation';

import { OBJECT_DEFS } from '../../objects/objectDefs';
import { OTHER_FIELDS } from '../../objects/schemaDetails';
import {
  activityColumns, asLabel, column, createdByField, documentColumns, field as plainField, lastModifiedByField, listColumnsOf, lookup,
  related, type Row, schemaRecord,
} from './kit';

const def = OBJECT_DEFS.meetings;

/**
 * Its fields come from the database (the server's SCHEMA_OBJECTS), shown by
 * their type; any column not placed below appears in Other Fields (MOTHER-299).
 * The name is shown by its title field, and the accounts and projects by their
 * related lists, so those three columns stay off the page.
 */
const field = schemaRecord('meetings', { hidden: ['name', 'account_name', 'project_name'] });

/** The server's name for the meeting: its title, else "<Category> Meeting · <date>". */
const meetingName = (row: Row): string => (typeof row.name === 'string' && row.name ? row.name : 'Meeting');

const details = [
  // Shows the name (the title, else its fallback); editing changes the title.
  { ...plainField('title', 'Meeting Name', { wide: true }), render: (row: Row) => meetingName(row) },
  field('meeting_date', 'Date'),
  field('meeting_category', 'Category'),
  field('status', 'Status'),
  field('sentiment', 'Sentiment'),
  field('internal_participants', 'Internal Participants', { wide: true }),
  field('external_participants', 'External Participants', { wide: true }),
  field('client_health', 'Client Health'),
  field('summary', 'Summary', { wide: true }),
  field('key_topics', 'Key Topics', { wide: true }),
  field('action_items', 'Action Items', { wide: true }),
  field('objections', 'Objections', { wide: true }),
  field('competitors', 'Competitors', { wide: true }),
  field('created_at', 'Created'),
  createdByField(),
  lastModifiedByField(),
];

/** A meeting: a client or internal meeting from transcript ingestion. Rationale: meetings.md. */
export const meetings: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Meeting',
  plural: def.label,
  icon: def.icon,
  listColumns: listColumnsOf(def, details),
  details,
  sections: [
    { key: 'overview', fields: ['title', 'meeting_date', 'meeting_category', 'status', 'sentiment', 'internal_participants', 'external_participants', 'client_health'] },
    { key: 'summary', fields: ['summary', 'key_topics', 'action_items', 'objections', 'competitors'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'last_modified_by_name'] },
  ],
  related: [
    related('accounts', 'Accounts', 'accounts', [
      column('name', 'Account', { primary: true }),
      column('status', 'Status', { format: asLabel }),
    ]),
    related('opportunities', 'Opportunities', 'opportunities', [
      column('name', 'Opportunity', { primary: true }),
      column('stage', 'Stage', { format: asLabel }),
    ]),
    related('projects', 'Projects', 'projects', [
      column('name', 'Project', { primary: true }),
      column('code', 'Code'),
    ]),
    related('contacts', 'Contacts', 'contacts', [
      column('name', 'Name', { primary: true }),
      column('title', 'Title'),
      column('account_name', 'Account', { link: lookup('accounts', 'account_id') }),
    ]),
    related('activities', 'Activities', 'activities', activityColumns()),
    related('documents', 'Documents', 'documents', documentColumns()),
  ],
  // Transcript is ObjectRecordView's own tab, placed after Summary. Other Fields (columns placed nowhere above,
  // MOTHER-299) shows on Overview, before System; until /meta answers there is no such section, and none shows.
  tabs: [
    {
      key: 'overview', label: 'Overview', details: true, sections: ['overview', OTHER_FIELDS.key, 'system'], layout: 'split',
      related: ['accounts', 'opportunities', 'projects', 'contacts'],
    },
    { key: 'summary', label: 'Summary', details: true, sections: ['summary'] },
    { key: 'follow-up', label: 'Follow-Up', related: ['activities', 'documents'] },
  ],
  // The server's name: the title, else "<Category> Meeting · <date>".
  titleOf: meetingName,
  searchKeys: ['name', 'account_name', 'summary'],
};
